import React, { useMemo, useState } from 'react';
import { fetchPromptDeRun, guardarVeredicto } from './api';
import CasoCard from './CasoCard';
import EtiquetaEditable from './EtiquetaEditable';
import { accionDe, esRehacer, formatearUsd, resumenPorTipoCaso } from './metricas';
import ReglasDecision from './ReglasDecision';
import ResultadoResumen from './ResultadoResumen';
import ResumenMetricas from './ResumenMetricas';
import type { ConfigAnalisis, EstadoVeredicto, PlantillaCorrida, Resultado, RunDetalle, VeredictoHumano } from './tipos';
import { Aviso, ChipStatus, btnSecundario, formatearFecha, inputChicoCls } from './ui';
import { useGuardadoCoordinado } from './useGuardadoCoordinado';

type Filtro = 'todos' | 'editadas' | 'control' | 'rehacer' | 'pedir_fotos' | 'fallidas';
const FILTROS: Array<{ id: Filtro; label: string }> = [
  { id: 'todos', label: 'Todos' },
  { id: 'editadas', label: 'Solo editadas' },
  { id: 'control', label: 'Solo control' },
  { id: 'rehacer', label: 'Solo rehacer' },
  { id: 'pedir_fotos', label: 'Solo pedir fotos' },
  { id: 'fallidas', label: 'Solo fallidas' },
];

/**
 * Detalle de una corrida: métricas por tipo de caso arriba, el prompt exacto a pedido,
 * filtros rápidos y una tarjeta por caso con lo que dijo el modelo + veredicto humano.
 * El estado (poll, recarga) vive en AnalisisCorridas; acá solo se muestra y se vota.
 */
const DetalleCorrida: React.FC<{
  detalle: RunDetalle;
  config: ConfigAnalisis;
  onVeredicto: (imagenId: string, v: VeredictoHumano | null) => void;
  /** "Reutilizar configuración": misma tanda + mismo modelo/effort/fotos/prompt, listos en Casos. */
  onReutilizar: (plantilla: PlantillaCorrida) => void;
  /** Renombrar la corrida (tira si falla; el estado lo actualiza el padre). */
  onRenombrar: (nueva: string) => Promise<void>;
}> = ({ detalle, config, onVeredicto, onReutilizar, onRenombrar }) => {
  const { run, resultados, casos } = detalle;
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [prompt, setPrompt] = useState<{ abierto: boolean; texto: string | null; cargando: boolean; error: string | null }>({
    abierto: false,
    texto: null,
    cargando: false,
    error: null,
  });

  const [reutilizar, setReutilizar] = useState<{ cargando: boolean; error: string | null }>({ cargando: false, error: null });

  const resumen = useMemo(() => resumenPorTipoCaso(resultados, config.fidelidad_max), [resultados, config.fidelidad_max]);
  const porId = useMemo(() => new Map(resultados.map((r) => [r.imagen_id, r])), [resultados]);

  const visibles = casos.filter((c) => {
    const r = porId.get(c.imagen_id);
    switch (filtro) {
      case 'editadas':
        return c.tipo_caso === 'editada';
      case 'control':
        return c.tipo_caso === 'control';
      case 'rehacer':
        return !!r && esRehacer(r);
      case 'pedir_fotos':
        return !!r && accionDe(r) === 'pedir_fotos';
      case 'fallidas':
        return !!r && !!r.error_analisis;
      default:
        return true;
    }
  });

  const verPrompt = async () => {
    if (prompt.abierto) return setPrompt((p) => ({ ...p, abierto: false }));
    if (prompt.texto !== null) return setPrompt((p) => ({ ...p, abierto: true }));
    setPrompt({ abierto: true, texto: null, cargando: true, error: null });
    try {
      const texto = await fetchPromptDeRun(run.run_id);
      setPrompt({ abierto: true, texto, cargando: false, error: null });
    } catch (e) {
      console.error('[analisis] prompt de la corrida falló:', e);
      setPrompt({ abierto: true, texto: null, cargando: false, error: e instanceof Error ? e.message : String(e) });
    }
  };

  // El prompt exacto (override o default) sale del mismo endpoint que "Ver prompt"; si ya
  // se pidió, se reusa. La etiqueta sugerida es "<original> v2" para que se note el linaje.
  const reutilizarConfig = async () => {
    setReutilizar({ cargando: true, error: null });
    try {
      const texto = prompt.texto ?? (await fetchPromptDeRun(run.run_id));
      setPrompt((p) => (p.texto === null ? { ...p, texto } : p));
      onReutilizar({
        etiqueta: `${run.etiqueta} v2`,
        modelo: run.modelo,
        effort: run.effort,
        fotos_producto: run.fotos_producto,
        prompt: texto,
        casos,
        origen: { run_id: run.run_id, etiqueta: run.etiqueta },
      });
      setReutilizar({ cargando: false, error: null });
    } catch (e) {
      console.error('[analisis] reutilizar configuración falló:', e);
      setReutilizar({ cargando: false, error: e instanceof Error ? e.message : String(e) });
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h3 className="font-semibold text-brand-black">
            <EtiquetaEditable valor={run.etiqueta} onGuardar={onRenombrar} inputClassName="w-64 font-semibold" />
          </h3>
          <ChipStatus status={run.status} />
          <span className="text-xs text-gray-500">
            {run.modelo}
            {run.effort ? ` · effort ${run.effort}` : ''} · fotos {run.fotos_producto} · {run.prompt_override ? 'prompt propio' : 'prompt default'} ·{' '}
            {formatearFecha(run.created_at)}
            {run.finished_at ? ` → ${formatearFecha(run.finished_at)}` : ''}
          </span>
          <span className="text-xs text-gray-500">
            <b className="text-brand-black">{run.hechas}</b> / {run.total}
            {run.fallidas > 0 && <span className="text-red-600"> · {run.fallidas} fallidas</span>} · {formatearUsd(run.cost_usd)}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={() => void verPrompt()} className={`${btnSecundario} py-1.5 px-3 text-xs`}>
              {prompt.abierto ? 'Ocultar prompt' : 'Ver prompt'}
            </button>
            <button
              type="button"
              onClick={() => void reutilizarConfig()}
              disabled={reutilizar.cargando || casos.length === 0}
              title="Vuelve a Casos con esta tanda y esta configuración (modelo, effort, fotos, prompt) prellenadas"
              className={`${btnSecundario} py-1.5 px-3 text-xs`}
            >
              {reutilizar.cargando ? 'Preparando…' : 'Reutilizar configuración'}
            </button>
          </div>
        </div>
        <Aviso error={reutilizar.error} className="mb-3" />
        <div className="mb-3">
          <ReglasDecision config={config} />
        </div>
        {prompt.abierto && (
          <div className="mb-3">
            {prompt.cargando && <p className="text-xs text-gray-500">Cargando prompt…</p>}
            <Aviso error={prompt.error} />
            {prompt.texto !== null && (
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 font-mono text-[11px] leading-snug text-brand-black">
                {prompt.texto}
              </pre>
            )}
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <ResumenMetricas titulo="Editadas (rehacer esperado)" columnas={[{ titulo: 'Editadas', resumen: resumen.editada }]} />
          <ResumenMetricas titulo="Control (rehacer = falso positivo)" columnas={[{ titulo: 'Control', resumen: resumen.control }]} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTROS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFiltro(f.id)}
            className={`rounded-full py-1.5 px-3 text-xs font-medium ${filtro === f.id ? 'bg-brand-black text-white' : 'border border-gray-300 bg-white text-brand-black'}`}
          >
            {f.label}
          </button>
        ))}
        <span className="text-xs text-gray-500">{visibles.length} casos</span>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {visibles.map((c) => {
          const r = porId.get(c.imagen_id);
          return (
            <CasoCard key={c.imagen_id} caso={c}>
              <div className="mt-auto space-y-2 border-t border-gray-100 pt-2">
                <ResultadoResumen resultado={r} fidelidadMax={config.fidelidad_max} />
                {r && !r.error_analisis && <Veredicto runId={run.run_id} resultado={r} onGuardado={onVeredicto} />}
              </div>
            </CasoCard>
          );
        })}
      </div>
      {visibles.length === 0 && <p className="text-sm text-gray-500">Nada para mostrar con este filtro.</p>}
    </div>
  );
};

const OPCIONES: Array<{ id: EstadoVeredicto; label: string; activo: string }> = [
  { id: 'correcto', label: 'Correcto', activo: 'bg-green-600 text-white' },
  { id: 'parcial', label: 'Parcial', activo: 'bg-amber-500 text-white' },
  { id: 'incorrecto', label: 'Incorrecto', activo: 'bg-red-600 text-white' },
];

/** ¿El modelo acertó? Click de nuevo en el activo lo borra (estado null). La nota guarda al
 *  blur; click y blur se coordinan en useGuardadoCoordinado (el blur del input dispara ANTES
 *  del click del botón). */
const Veredicto: React.FC<{
  runId: string;
  resultado: Resultado;
  onGuardado: (imagenId: string, v: VeredictoHumano | null) => void;
}> = ({ runId, resultado, onGuardado }) => {
  const g = useGuardadoCoordinado<EstadoVeredicto>({
    accion: resultado.veredicto_humano?.estado ?? null,
    texto: resultado.veredicto_humano?.nota ?? '',
    version: resultado.veredicto_humano?.at,
    guardar: async (nuevo, nota) => {
      await guardarVeredicto(runId, resultado.imagen_id, nuevo, nota);
      onGuardado(resultado.imagen_id, nuevo ? { estado: nuevo, nota: nota || null, at: new Date().toISOString() } : null);
    },
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      {OPCIONES.map((o) => (
        <button
          key={o.id}
          type="button"
          onMouseDown={g.onMouseDownBoton}
          onClick={() => void g.elegir(o.id)}
          className={`rounded-full py-1.5 px-3 text-xs font-medium ${g.accion === o.id ? o.activo : 'border border-gray-300 bg-white text-brand-black'}`}
        >
          {o.label}
        </button>
      ))}
      <input
        value={g.texto}
        onChange={(e) => g.setTexto(e.target.value)}
        onBlur={g.onBlur}
        placeholder={g.accion ? 'Nota (guarda al salir)' : 'Nota (elegí un veredicto primero)'}
        className={`${inputChicoCls} min-w-40 flex-1 text-xs`}
      />
      {g.guardando && <span className="text-[11px] text-gray-500">Guardando…</span>}
      {g.error && <span className="text-xs text-red-600">{g.error}</span>}
    </div>
  );
};

export default DetalleCorrida;
